<template>
  <div>
    <v-app-bar class="text-center" :elevation="1">
      <!-- Clickable Logo -->
      <v-img
        :src="require('@/assets/suuq.logo.svg')"
        alt="SUUQ Logo"
        class="ml-8"
        @click="logoRoute"
        cover
        max-width="100"
      />
    </v-app-bar>

    <div class="d-flex align-center justify-center" style="height: 90vh">
      <v-card elevation="0" width="400" class="mx-auto">
        <v-card-title class="text-center mb-5">
          Create an account
        </v-card-title>

        <v-card-text>
          <v-form
            ref="form"
            v-model="formValid"
            @submit.prevent="save"
            class="ma-3"
          >
            <v-text-field
              v-model="editedItem.fullName"
              :rules="[rules.fullName]"
              name="fullName"
              label="Name"
              variant="outlined"
            />

            <v-text-field
              v-model="editedItem.email"
              :rules="[rules.email]"
              name="email"
              label="Email"
              variant="outlined"
              class="mt-1"
            />

            <v-text-field
              v-model="editedItem.phoneNumber"
              :rules="[rules.phonenumber]"
              name="phoneNumber"
              label="Phone Number"
              variant="outlined"
              class="mt-1"
            />

            <v-text-field
              v-model="editedItem.password"
              :appendInnerIcon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
              :rules="[rules.required, rules.min]"
              :type="show1 ? 'text' : 'password'"
              label="Password"
              hint="At least 8 characters"
              counter
              variant="outlined"
              @click:append-inner="show1 = !show1"
            />

            <v-text-field
              v-model="editedItem.password_confirmation"
              :appendInnerIcon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
              :rules="[rules.required, rules.min, rules.passwordMatch]"
              :type="show2 ? 'text' : 'password'"
              label="Confirm Password"
              hint="Must match the password above"
              counter
              variant="outlined"
              @click:append-inner="show2 = !show2"
            />

            <v-btn
              :loading="loading"
              block
              color="#b8860b"
              size="large"
              type="submit"
              elevation="0"
              class="mt-2"
              :disabled="!acceptTerms || loading"
              style="text-transform: none;"
            >
              <span class="text-white"> Sign Up </span>
            </v-btn>

            <!-- Inline terms row -->
            <div class="terms-row">
              <span class="text-grey text-caption">By signing up I agree to the</span>

              <v-checkbox
                v-model="acceptTerms"
                :rules="[rules.acceptTerms]"
                hide-details
                density="compact"
                color="primary"
                class="inline-checkbox"
                :ripple="false"
                aria-label="Accept Terms"
              />

              <span class="nowrap">
                <v-btn
                  @click="terms"
                  variant="text"
                  class="text-none"
                  size="x-small"
                  color="blue"
                >
                  Terms and Conditions
                </v-btn>
                <span class="text-grey">&nbsp;and&nbsp;</span>
                <v-btn
                  @click="policies"
                  variant="text"
                  class="text-none"
                  size="x-small"
                  color="blue"
                >
                  Privacy Policies
                </v-btn>
              </span>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import AuthService from "@/services/auth.service";

export default {
  data() {
    return {
      show1: false,
      show2: false,
      loading: false,
      formValid: false,
      acceptTerms: false,

      editedItem: {
        fullName: "",
        lastname: "",
        phoneNumber: "",   // <-- matches the input v-model
        email: "",
        password: "",
        password_confirmation: "",
      },
      defaultItem: {
        fullName: "",
        lastname: "",
        phoneNumber: "",
        email: "",
        password: "",
        password_confirmation: "",
      },

      rules: {
        // shared helpers
        required: (v) => (!!v || v === 0) || "Required",
        min: (v) => (v?.length ?? 0) >= 8 || "Min 8 characters",

        // field-specific
        email: (v) => !!v || "Email required",
        fullName: (v) => !!v || "First name required",
        phonenumber: (v) => !!v || "Phone number required",
        passwordMatch: (v) =>
          v === (this.editedItem?.password ?? "") || "Passwords do not match",
        acceptTerms: (v) => v === true || "Please accept the Terms to continue",
      },
    };
  },

  methods: {
    logoRoute() {
      this.$router.push(this.$route.query.redirect || "/");
    },
    terms() {
      this.$router.push(this.$route.query.redirect || "/terms");
    },
    policies() {
      this.$router.push(this.$route.query.redirect || "/policies");
    },

    formatPhoneNumber(phoneNumber) {
      const pattern = /^(0|254|\+254)([1-9]\d{8})$/;
      if (pattern.test(phoneNumber)) {
        if (phoneNumber.startsWith("0")) return "+254" + phoneNumber.slice(1);
        return "+254" + phoneNumber.slice(-9);
      } else if (/^[71]/.test(phoneNumber)) {
        return "+254" + phoneNumber;
      }
      return phoneNumber;
    },

    async save() {
      if (!this.acceptTerms) {
        this.$store.dispatch("alert/error", "Please accept the Terms to continue");
        return;
      }

      // Validate all fields
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      this.loading = true;

      // Optional: normalize phone number before submit
      // this.editedItem.phoneNumber = this.formatPhoneNumber(this.editedItem.phoneNumber);

      // Backend expects roleId=3 for self-register
      const payload = { ...this.editedItem, roleId: 3 };

      try {
        const response = await AuthService.selfRegister(payload);

        if (response.status === 201) {
          const user = response.data.data;
          localStorage.setItem("verification_email", user.email);
          this.$store.dispatch("alert/success", response.data.message);
          this.$router.push(this.$route.query.redirect || "/verify-user");
        } else {
          this.$store.dispatch("alert/error", response.data.message);
        }
      } catch (error) {
        if (error?.response?.status === 401) {
          this.$router.push("/login");
        }
        this.$store.dispatch(
          "alert/error",
          error?.response?.data?.message || error.message || "Registration failed"
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.terms-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  line-height: 1.2;
  margin-top: 10px;
}
.nowrap { white-space: nowrap; }

.inline-checkbox {
  margin: 0 2px !important;
  padding: 0 !important;
  transform: translateY(-1px);
}
.inline-checkbox .v-input__control { min-height: 0 !important; }
.inline-checkbox .v-selection-control { height: 18px !important; }
.inline-checkbox .v-selection-control__wrapper { width: 16px; height: 16px; }
.inline-checkbox .v-icon { font-size: 12px; }
</style>
